import type {
  ReadinessBreakdown,
  ReadinessComponentResult,
  ReadinessFormula,
  ReadinessPillarResult,
} from '@/lib/api/readiness';

/** 0..1 weight -> "70%". Never invent precision the formula doesn't have. */
export function weightPct(weight: number): string {
  return `${Math.round(weight * 100)}%`;
}

/**
 * A component's weight in the whole score, i.e. after its pillar's own share
 * and any renormalisation. Returns 0..1.
 */
export function overallWeight(
  pillar: ReadinessPillarResult,
  component: ReadinessComponentResult,
): number {
  return pillar.effective_weight * component.effective_weight;
}

/** True when renormalisation actually moved a weight (>= 1 percentage point). */
export function isRescaled(nominal: number, effective: number): boolean {
  return Math.abs(Math.round(nominal * 100) - Math.round(effective * 100)) >= 1;
}

/**
 * Human label for a tier key as the API defines it. Returns undefined when the
 * formula hasn't loaded (or doesn't know the key) so callers decide their own
 * fallback rather than inheriting a guess.
 */
export function tierLabel(
  tier: string | undefined,
  formula?: ReadinessFormula | null,
): string | undefined {
  if (!tier) return undefined;
  return formula?.tiers.find((t) => t.key === tier)?.label;
}

/** Last-resort display of a tier key when the formula is unavailable. */
export function prettifyTierKey(tier: string | undefined): string {
  if (!tier) return '';
  return tier.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Tier bands as inclusive ranges, highest first — for "how bands are set". */
export function tierBands(
  formula: ReadinessFormula,
): { key: string; label: string; range: string }[] {
  const sorted = [...formula.tiers].sort((a, z) => z.min_score - a.min_score);
  return sorted.map((t, i) => {
    const upper = i === 0 ? 100 : sorted[i - 1].min_score - 1;
    return { key: t.key, label: t.label, range: `${t.min_score}–${upper}` };
  });
}

/**
 * The points a component could still recover, i.e. what closing it entirely
 * would add to the score: `(100 - value) x pillar weight x component weight`.
 *
 * This is the number a placement officer actually acts on — "attendance is 33"
 * says nothing about whether fixing it is worth the conversation.
 */
export function recoverablePoints(
  pillar: ReadinessPillarResult,
  component: ReadinessComponentResult,
): number {
  if (pillar.excluded || component.excluded || component.value === null) return 0;
  return (100 - component.value) * overallWeight(pillar, component);
}

/**
 * The one or two components worth intervening on, keyed by component key with
 * their whole-point upside. Deliberately short: flagging every row would read
 * as decoration rather than a recommendation.
 */
export function topLevers(
  breakdown: ReadinessBreakdown,
  limit = 2,
): Map<string, number> {
  const ranked = breakdown.pillars
    .flatMap((pillar) =>
      pillar.components.map((component) => ({
        key: component.key,
        points: Math.round(recoverablePoints(pillar, component)),
      })),
    )
    // A sub-point lever is noise: it would tell a TPO to chase nothing.
    .filter((c) => c.points >= 1)
    .sort((a, z) => z.points - a.points)
    .slice(0, limit);
  return new Map(ranked.map((c) => [c.key, c.points]));
}

// `evidenceTotalFromSummary` lived here: it read the full count back out of
// the summary sentence ("2 of 6 sessions held attended" -> 6) because the API
// sent no count of its own. The payload now carries `counts.total`, so the
// parser is gone rather than kept as a second, guessable source of the same
// number.

/** Did anything get excluded for want of data? Drives the renormalisation note. */
export function hasExclusions(breakdown: ReadinessBreakdown): boolean {
  return breakdown.pillars.some((p) => p.excluded || p.components.some((c) => c.excluded));
}

const AS_OF_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "as of" timestamp text; empty string when the API didn't send one. */
export function formatComputedAt(computedAt?: string | null): string {
  if (!computedAt) return '';
  const d = new Date(computedAt);
  if (Number.isNaN(d.getTime())) return '';
  return AS_OF_FMT.format(d);
}
